/**
 * 设计中心模版业务层 — 对接 Apifox scj 设计中心模版 Controller（文档 13）
 */

import { designCenterTemplateService } from '@/services/artApi';
import {
  unwrapApiList,
  unwrapApiResult,
  unwrapApiTotalElements,
  mapDesignCenterTemplateDtoToPlatform,
  mapPlatformDesignCenterToRequestPayload,
  toFullEditorLayerJson,
  DESIGN_CENTER_TEMPLATE_TYPE,
  DESIGN_CENTER_OP_TYPE,
} from '@/utils/artMapper';
import {
  buildDepartmentOptions,
  getDepartmentRequestParams,
  getMaterialDepartmentContext,
  getMaterialDepartmentIdParam,
  resolveSyncStoreIds,
  toPlatformStoreId,
  resolveShopId,
  resolveUserId,
} from '@/utils/materialApiContext';

export { DESIGN_CENTER_TEMPLATE_TYPE, DESIGN_CENTER_OP_TYPE };

/** 将搜索框关键词映射为列表接口的模糊查询字段 */
export function buildDesignCenterSearchParams(query = '') {
  const keyword = String(query || '').trim();
  if (!keyword) return {};
  return {
    searchDesignName: keyword,
    searchTemplateName: keyword,
  };
}

/** 列表查询 opType 与保存接口相反：列表 1=已保存，2=草稿；保存仍是 1=草稿，2=正式保存 */
export const DESIGN_CENTER_LIST_OP_TYPE = {
  SAVED: 1,
  DRAFT: 2,
};

function listTemplateTypeForOpType(opType) {
  if (opType === DESIGN_CENTER_LIST_OP_TYPE.SAVED) return DESIGN_CENTER_TEMPLATE_TYPE.SAVED;
  if (opType === DESIGN_CENTER_LIST_OP_TYPE.DRAFT) return DESIGN_CENTER_TEMPLATE_TYPE.DRAFT;
  return undefined;
}

function storeIdFromContext(storeId, user, stores = []) {
  return toPlatformStoreId(resolveShopId(storeId, user, stores));
}

function departmentContext(storeId, user, apiContext = {}) {
  const { stores, sessionDept } = buildDepartmentOptions({ storeId, user, ...apiContext });
  return {
    stores,
    dept: getMaterialDepartmentContext(storeId, user, stores, sessionDept),
    deptParams: getMaterialDepartmentIdParam(storeId, user, stores, sessionDept),
    requestParams: getDepartmentRequestParams(storeId, user, stores, sessionDept),
  };
}

function positiveIntegerOrDefault(value, fallback) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export async function fetchDesignCenterList(storeId, user, query = {}, apiContext = {}) {
  const { stores, deptParams } = departmentContext(storeId, user, apiContext);
  const sid = storeIdFromContext(storeId, user, stores);
  const requestedOpType = query.opType != null ? Number(query.opType) : undefined;
  const requestedTemplateType = query.templateType != null
    ? Number(query.templateType)
    : listTemplateTypeForOpType(requestedOpType);

  const payload = await designCenterTemplateService.list({
    ...deptParams,
    pageNum: positiveIntegerOrDefault(query.pageNum, 1),
    pageSize: positiveIntegerOrDefault(query.pageSize, 200),
    opType: requestedOpType,
    templateType: requestedTemplateType,
    searchDesignName: query.searchDesignName || undefined,
    searchTemplateName: query.searchTemplateName || undefined,
    createUserId: query.createUserId != null ? Number(query.createUserId) : undefined,
  });

  const totalElements = unwrapApiTotalElements(payload);

  const list = unwrapApiList(payload).map((dto) => {
    const mapped = mapDesignCenterTemplateDtoToPlatform(dto, sid);
    if (requestedOpType === DESIGN_CENTER_LIST_OP_TYPE.SAVED || requestedTemplateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED) {
      return {
        ...mapped,
        templateType: DESIGN_CENTER_TEMPLATE_TYPE.SAVED,
        status: 'done',
      };
    }
    if (requestedOpType === DESIGN_CENTER_LIST_OP_TYPE.DRAFT || requestedTemplateType === DESIGN_CENTER_TEMPLATE_TYPE.DRAFT) {
      return {
        ...mapped,
        templateType: DESIGN_CENTER_TEMPLATE_TYPE.DRAFT,
        status: 'draft',
      };
    }
    return mapped;
  });

  list.totalElements = totalElements;
  return list;
}

export async function syncDesignCenterLibrary(draftsRef, storeId, user, options = {}) {
  const {
    localSavedDesigns = [],
    countsRef,
    searchTotalsRef,
    searchTotalKey,
    pageNum,
    pageSize,
    searchDesignName,
    searchTemplateName,
    createUserId,
    ...apiContext
  } = options;
  const requestedOpType = Number(options.opType);
  const isTargetedTabRequest = [
    DESIGN_CENTER_LIST_OP_TYPE.SAVED,
    DESIGN_CENTER_LIST_OP_TYPE.DRAFT,
  ].includes(requestedOpType);
  const storeIds = resolveSyncStoreIds({ storeId, user, ...apiContext });

  let remote = [];
  const fetchedCounts = { saved: null, drafts: null };
  const listQuery = {
    pageNum: positiveIntegerOrDefault(pageNum, 1),
    pageSize: positiveIntegerOrDefault(
      pageSize,
      (searchDesignName || searchTemplateName) ? 10 : 200,
    ),
    searchDesignName: searchDesignName || undefined,
    searchTemplateName: searchTemplateName || undefined,
    createUserId: createUserId != null ? Number(createUserId) : undefined,
  };

  try {
    const opTypes = isTargetedTabRequest
      ? [requestedOpType]
      : [DESIGN_CENTER_LIST_OP_TYPE.SAVED, DESIGN_CENTER_LIST_OP_TYPE.DRAFT];
    const results = await Promise.all(
      storeIds.flatMap((sid) => opTypes.map(async (opType) => {
        const list = await fetchDesignCenterList(sid, user, {
          opType,
          templateType: listTemplateTypeForOpType(opType),
          ...listQuery,
        }, apiContext);
        return { opType, list };
      })),
    );
    const seenRemote = new Set();
    results.forEach(({ opType, list }) => {
      if (list?.totalElements != null) {
        if (opType === DESIGN_CENTER_LIST_OP_TYPE.SAVED) {
          fetchedCounts.saved = (fetchedCounts.saved || 0) + list.totalElements;
        } else if (opType === DESIGN_CENTER_LIST_OP_TYPE.DRAFT) {
          fetchedCounts.drafts = (fetchedCounts.drafts || 0) + list.totalElements;
        }
      }
      if (searchTotalsRef && isTargetedTabRequest) {
        const total = list?.totalElements ?? list.length;
        if (searchTotalKey === 'drafts' && opType === DESIGN_CENTER_LIST_OP_TYPE.DRAFT) {
          searchTotalsRef.drafts = total;
        } else if (searchTotalKey !== 'drafts' && opType === DESIGN_CENTER_LIST_OP_TYPE.SAVED) {
          searchTotalsRef.saved = total;
        }
      }
      list.forEach((item) => {
        if (!item?.id || seenRemote.has(item.id)) return;
        seenRemote.add(item.id);
        remote.push(item);
      });
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[art-api] 设计中心远程列表加载失败，保留本地已保存设计', error);
    }
    return draftsRef.value || [];
  }

  if (countsRef && typeof countsRef === 'object') {
    if (fetchedCounts.saved != null) countsRef.saved = fetchedCounts.saved;
    if (fetchedCounts.drafts != null) countsRef.drafts = fetchedCounts.drafts;
  }

  if (isTargetedTabRequest) {
    const replacingSaved = requestedOpType === DESIGN_CENTER_LIST_OP_TYPE.SAVED;
    const preservedOtherTab = (draftsRef.value || []).filter((item) => {
      const isSaved = item?.templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED || item?.status === 'done';
      return replacingSaved ? !isSaved : isSaved;
    });
    const seen = new Set();
    draftsRef.value = [...remote, ...preservedOtherTab].filter((item) => {
      const id = String(item?.id || '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return draftsRef.value;
  }

  const existingLocalDrafts = (draftsRef.value || []).filter(item =>
    item?.id
    && item.templateType !== DESIGN_CENTER_TEMPLATE_TYPE.SAVED
    && item.status !== 'done',
  );

  const merged = [];
  const seen = new Set();
  [...remote, ...localSavedDesigns, ...existingLocalDrafts].forEach((item) => {
    const id = String(item?.id || '');
    if (!id || seen.has(id)) return;
    seen.add(id);
    merged.push({
      ...item,
      templateType: item.templateType ?? (
        item.status === 'done' ? DESIGN_CENTER_TEMPLATE_TYPE.SAVED : DESIGN_CENTER_TEMPLATE_TYPE.DRAFT
      ),
      status: item.status ?? (
        item.templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED ? 'done' : 'draft'
      ),
    });
  });
  draftsRef.value = merged;
  return merged;
}

function normalizeRequiredOpType(value) {
  const opType = Number(value);
  if (![DESIGN_CENTER_OP_TYPE.DRAFT, DESIGN_CENTER_OP_TYPE.SAVE].includes(opType)) {
    throw new Error('保存设计字段 opType 必须为 1 或 2');
  }
  return opType;
}

function requireHttpCoverThumbUrl(value) {
  const coverThumbUrl = String(value || '').trim();
  if (!/^https?:\/\//i.test(coverThumbUrl)) {
    throw new Error('缺少最新封面 coverThumbUrl，无法保存母模版');
  }
  return coverThumbUrl;
}

function requireFullLayerJson(value) {
  const layerJson = toFullEditorLayerJson(value);
  if (!layerJson) {
    throw new Error('缺少全量分层数据 layerJson，无法保存母模版');
  }
  return layerJson;
}

function mapSaveOrUpdateResult(payload, requestBody, department, storeId) {
  const result = unwrapApiResult(payload);
  const responseDto = result && typeof result === 'object'
    ? result
    : (result != null && result !== '' ? { id: result } : {});
  return mapDesignCenterTemplateDtoToPlatform({
    ...requestBody,
    ...responseDto,
    departmentId: responseDto.departmentId ?? department.departmentId,
    departmentName: responseDto.departmentName ?? department.departmentName,
  }, storeId);
}

export async function createDesignCenterRemote(storeId, user, input = {}, stores = [], options = {}) {
  const { silentError, ...apiContext } = options;
  const { dept, requestParams } = departmentContext(storeId, user, { stores, ...apiContext });
  const sid = storeIdFromContext(storeId, user, stores);
  const body = mapPlatformDesignCenterToRequestPayload(
    {
      designName: input.designName || '未命名设计',
      templateName: input.templateName || input.designName || '未命名设计',
      templateId: input.templateId,
      layerJson: input.layerJson,
      state: input.state,
      originFileUrl: input.originFileUrl,
      createUserId: resolveUserId(user),
    },
    input.templateType ?? DESIGN_CENTER_TEMPLATE_TYPE.DRAFT,
  );
  body.opType = normalizeRequiredOpType(input.opType ?? DESIGN_CENTER_OP_TYPE.DRAFT);
  const payload = silentError != null
    ? await designCenterTemplateService.saveOrUpdate(body, requestParams, { silentError })
    : await designCenterTemplateService.saveOrUpdate(body, requestParams);
  return mapSaveOrUpdateResult(payload, body, dept, sid);
}

export async function updateDesignCenterRemote(storeId, user, design = {}, stores = [], options = {}) {
  const { silentError, ...apiContext } = options;
  const { dept, requestParams } = departmentContext(storeId, user, { stores, ...apiContext });
  const sid = storeIdFromContext(storeId, user, stores);
  const templateType = design.templateType ?? (
    design.status === 'done'
      ? DESIGN_CENTER_TEMPLATE_TYPE.SAVED
      : DESIGN_CENTER_TEMPLATE_TYPE.DRAFT
  );
  const body = mapPlatformDesignCenterToRequestPayload({
    ...design,
    createUserId: design.createUserId ?? resolveUserId(user),
  }, templateType);
  if (body.id == null) throw new Error('缺少设计中心模版 ID');
  body.opType = normalizeRequiredOpType(design.opType ?? (
    templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED
      ? DESIGN_CENTER_OP_TYPE.SAVE
      : DESIGN_CENTER_OP_TYPE.DRAFT
  ));
  const payload = silentError != null
    ? await designCenterTemplateService.saveOrUpdate(body, requestParams, { silentError })
    : await designCenterTemplateService.saveOrUpdate(body, requestParams);
  return mapSaveOrUpdateResult(payload, body, dept, sid);
}

/**
 * 保存母模版：POST /api/art/design-center-template/saveOrUpdate
 * 固定 opType=3，必须携带最新全量 layerJson 与新上传的 coverThumbUrl。
 */
export async function saveMasterTemplateRemote(storeId, user, input = {}, stores = [], options = {}) {
  const { silentError, ...apiContext } = options;
  const { dept, requestParams } = departmentContext(storeId, user, { stores, ...apiContext });
  const sid = storeIdFromContext(storeId, user, stores);
  const templateId = input.templateId ?? input.id;
  if (templateId == null || String(templateId).trim() === '') {
    throw new Error('缺少模版 ID，无法保存母模版');
  }
  const coverThumbUrl = requireHttpCoverThumbUrl(input.coverThumbUrl);
  const layerJson = requireFullLayerJson(input.state ?? input.layerJson);
  const templateName = String(input.templateName || input.name || input.designName || '').trim() || '未命名模版';
  const body = mapPlatformDesignCenterToRequestPayload({
    templateId,
    templateName,
    designName: input.designName || templateName,
    layerJson,
    coverThumbUrl,
    originFileUrl: input.originFileUrl || '',
    createUserId: input.createUserId ?? resolveUserId(user),
    opType: DESIGN_CENTER_OP_TYPE.UPDATE_TEMPLATE,
  });
  delete body.id;
  delete body.templateType;
  body.opType = DESIGN_CENTER_OP_TYPE.UPDATE_TEMPLATE;
  body.coverThumbUrl = coverThumbUrl;
  body.layerJson = layerJson;
  const payload = silentError != null
    ? await designCenterTemplateService.saveOrUpdate(body, requestParams, { silentError })
    : await designCenterTemplateService.saveOrUpdate(body, requestParams);
  return mapSaveOrUpdateResult(payload, body, dept, sid);
}

export async function deleteDesignCenterRemote(storeId, user, id, stores = [], apiContext = {}) {
  if (id == null || id === '') throw new Error('缺少设计中心模版 ID');
  const { requestParams } = departmentContext(storeId, user, { stores, ...apiContext });
  const payload = await designCenterTemplateService.delete({
    id: Number(id) || id,
    ...requestParams,
  });
  unwrapApiResult(payload);
}

export async function getDesignCenterDetailRemote(storeId, user, id, apiContext = {}) {
  if (id == null || id === '') {
    throw new Error('缺少设计中心模版 ID');
  }
  const { stores, deptParams } = departmentContext(storeId, user, apiContext);
  const sid = storeIdFromContext(storeId, user, stores);
  const payload = await designCenterTemplateService.get({
    id: Number(id) || id,
    ...deptParams,
  });
  const result = unwrapApiResult(payload);
  if (!result || result.id == null || result.id === '') {
    throw new Error('未获取到设计详情，请刷新设计中心后重试');
  }
  return mapDesignCenterTemplateDtoToPlatform(result, sid);
}
